import { AppState, BankAccount, DayResult, Plan, RagThresholds, TransactionOccurrence } from '../types'
import { computeCashflow, ragForBalance } from './cashflowEngine'
import { accountState } from './storage'

/**
 * What the app is looking at: one account, or every account added together.
 * Held by the UI rather than in the plan, so switching doesn't count as an edit.
 */
export type Scope = { kind: 'all' } | { kind: 'account'; account: BankAccount }

export const ALL_ACCOUNTS = 'all'

/** The accounts whose balances make up the combined total. */
export function includedAccounts(plan: Plan): BankAccount[] {
  return plan.accounts.filter((a) => a.includeInTotal)
}

/** The engine input for whatever the scope covers — one account, or several. */
export function statesInScope(plan: Plan, scope: Scope): AppState[] {
  if (scope.kind === 'account') return [accountState(plan, scope.account)]
  return includedAccounts(plan).map((a) => accountState(plan, a))
}

/**
 * Adds several accounts' projections together, day by day.
 *
 * Each account is projected on its own — from its own starting balance and
 * date — and the balances are summed. A day an account has no figure for
 * (before its starting date, with history projection off) counts it as
 * nothing: the account isn't being tracked yet, so it isn't in the total yet.
 * A day no account has a figure for is left out, as it is for a single account.
 *
 * Targets belong to the account they were set on and aren't carried across:
 * a savings target reached means nothing against the combined balance.
 */
export function combineResults(
  perAccount: { name: string; results: DayResult[] }[],
  thresholds: RagThresholds,
): DayResult[] {
  const byDate = new Map<string, { balance: number; netChange: number; transactions: TransactionOccurrence[] }>()
  for (const { name, results } of perAccount) {
    for (const day of results) {
      const entry = byDate.get(day.date) ?? { balance: 0, netChange: 0, transactions: [] }
      entry.balance += day.balance
      entry.netChange += day.netChange
      for (const tx of day.transactions) entry.transactions.push({ ...tx, accountName: name })
      byDate.set(day.date, entry)
    }
  }

  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, e]) => ({
      date,
      transactions: e.transactions,
      netChange: e.netChange,
      balance: e.balance,
      rag: ragForBalance(e.balance, thresholds),
      targetHits: [],
    }))
}

/** `computeCashflow`, for either a single account or the combined total. */
export function computeScope(
  plan: Plan,
  scope: Scope,
  viewStart: Date,
  viewEnd: Date,
  includeAdjustments = true,
): DayResult[] {
  if (scope.kind === 'account') {
    return computeCashflow(accountState(plan, scope.account), viewStart, viewEnd, includeAdjustments)
  }
  return combineResults(
    includedAccounts(plan).map((a) => ({
      name: a.name,
      results: computeCashflow(accountState(plan, a), viewStart, viewEnd, includeAdjustments),
    })),
    plan.settings.combinedRagThresholds,
  )
}

export interface BalanceSnapshot {
  /** Undefined when there's no figure for today — the account starts in the future. */
  today?: DayResult
  /** The lowest closing balance in the window ahead, and when it falls. */
  lowest?: DayResult
}

/** Where a run of results stands today, and the lowest point from today on. */
export function snapshotOf(results: DayResult[], todayIso: string): BalanceSnapshot {
  let today: DayResult | undefined
  let lowest: DayResult | undefined
  for (const day of results) {
    if (day.date === todayIso) today = day
    if (day.date >= todayIso && (!lowest || day.balance < lowest.balance)) lowest = day
  }
  return { today, lowest }
}
